import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Avatar, Chip, Button, Tabs, Tab, IconButton, Tooltip, Badge, Stack, Divider, Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, Alert, Snackbar } from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  Pets as PetsIcon,
  Edit as EditIcon,
  CalendarToday as CalendarIcon,
  LocalHospital as HospitalIcon,
  Receipt as ReceiptIcon,
  AttachFile as AttachFileIcon,
  FileDownload as DownloadIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Share as ShareIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import CreateAppointmentDialog from './CreateAppointmentDialog';
import StartVisitDialog from './StartVisitDialog';
import IssuePrescriptionDialog from './IssuePrescriptionDialog';
import AddDocumentDialog from './AddDocumentDialog';
import ExportPDFDialog from './ExportPDFDialog';
import AnimalReproTab from '@shared/AnimalReproTab';
import LitterDialog from '@shared/LitterDialog';
import PedigreeModal from '@shared/PedigreeModal';
import { formatForDisplay, formatDateOnly } from '@utils/dateFormatter';
import { animalsService } from '@services/animalsService';

// Types
interface Animal {
  id: string;
  name: string;
  species: string;
  breed: string;
  sex: 'male' | 'female';
  age: number;
  weight?: number;
  color?: string;
  microchip?: string;
  dateOfBirth?: string;
  status: 'active' | 'deceased' | 'transferred';
  clientId: string;
  clientName: string;
  ownerId: string;
  tags: string[];
  allergies: string[];
  temperament?: string;
  sensitivities?: string[];
  createdAt: string;
  updatedAt: string;
  clinicId: string;
  // Additional fields for detailed view
  markings?: string;
  spayedNeutered?: boolean;
  deceasedAt?: string;
  deceasedReason?: string;
  notes?: string;
  flags?: AnimalFlag[];
  vitals?: VitalRecord[];
  vaccinations?: VaccinationRecord[];
  prescriptions?: PrescriptionRecord[];
  documents?: DocumentRecord[];
  visits?: VisitRecord[];
  appointments?: AppointmentRecord[];
}

interface AnimalFlag {
  id: string;
  type: 'allergy' | 'warning' | 'chronic' | 'intolerance';
  label: string;
  severity: 'low' | 'medium' | 'high';
  notes?: string;
  createdAt: string;
  createdBy: string;
}

interface VitalRecord {
  id: string;
  type: 'weight' | 'temperature' | 'heart_rate' | 'respiratory_rate' | 'pain_score';
  value: number;
  unit: string;
  measuredAt: string;
  recordedBy: string;
}

interface VaccinationRecord {
  id: string;
  type: string;
  manufacturer: string;
  lot: string;
  date: string;
  veterinarian: string;
  validUntil: string;
  notes?: string;
}

interface PrescriptionRecord {
  id: string;
  medication: string;
  dosage: string;
  frequency: string;
  duration: string;
  status: 'draft' | 'issued' | 'expired';
  issuedAt: string;
  issuedBy: string;
  notes?: string;
}

interface DocumentRecord {
  id: string;
  name: string;
  type: string;
  size: number;
  uploadedAt: string;
  uploadedBy: string;
  tags: string[];
}

interface VisitRecord {
  id: string;
  date: string;
  type: string;
  veterinarian: string;
  status: string;
  summary?: string;
}

interface AppointmentRecord {
  id: string;
  date: string;
  time: string;
  type: string;
  status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled';
  notes?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`animal-tabpanel-${index}`}
      aria-labelledby={`animal-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface AnimalCardProps {
  animalId: string;
  onClose?: () => void;
}

const AnimalCard: React.FC<AnimalCardProps> = ({ animalId, onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [animal, setAnimal] = useState<Animal | null>(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [assignOwnerDialogOpen, setAssignOwnerDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  
  // Quick Actions states
  const [createAppointmentDialogOpen, setCreateAppointmentDialogOpen] = useState(false);
  const [startVisitDialogOpen, setStartVisitDialogOpen] = useState(false);
  const [issuePrescriptionDialogOpen, setIssuePrescriptionDialogOpen] = useState(false);
  const [addDocumentDialogOpen, setAddDocumentDialogOpen] = useState(false);
  const [exportPDFDialogOpen, setExportPDFDialogOpen] = useState(false);
  const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false);
  
  // Reproduction dialogs
  const [litterDialogOpen, setLitterDialogOpen] = useState(false);
  const [pedigreeModalOpen, setPedigreeModalOpen] = useState(false);
  
  // Sterilization states
  const [sterilizationStatus, setSterilizationStatus] = useState<boolean>(false);
  const [sterilizationDate, setSterilizationDate] = useState<string>('');
  const [savingSterilization, setSavingSterilization] = useState(false);
  
  // Name editing states
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    loadAnimalDetails();
  }, [animalId]);

  useEffect(() => {
    if (animal) {
      setSterilizationStatus(animal.spayedNeutered || false);
      setSterilizationDate(''); // TODO: Load from backend when field is available
      setNewName(animal.name);
    }
  }, [animal]);

  const loadAnimalDetails = async () => {
    try {
      setLoading(true);
      // TODO: Implement API call to get animal details
      // const response = await animalsService.getAnimalDetails(animalId);
      // setAnimal(response.data);
      
      // Mock data for now
      setAnimal({
        id: animalId,
        name: 'Rex',
        species: 'dog',
        breed: 'German Shepherd',
        sex: 'male',
        age: 3,
        weight: 25.5,
        color: 'Brown',
        microchip: '123456789',
        dateOfBirth: '2021-05-15',
        status: 'active',
        clientId: 'client-1',
        clientName: 'John Doe',
        ownerId: 'client-1',
        tags: ['friendly', 'vaccinated'],
        allergies: ['pollen'],
        temperament: 'calm',
        sensitivities: [],
        createdAt: '2021-05-15T00:00:00Z',
        updatedAt: '2024-09-25T00:00:00Z',
        clinicId: user?.clinicId || '',
        markings: 'White patch on chest',
        spayedNeutered: false,
        notes: 'Very friendly dog, loves children',
        flags: [
          {
            id: 'flag-1',
            type: 'allergy',
            label: 'Pollen Allergy',
            severity: 'medium',
            notes: 'Seasonal allergy, requires antihistamines',
            createdAt: '2024-01-15T00:00:00Z',
            createdBy: 'vet-1'
          }
        ],
        vitals: [],
        vaccinations: [],
        prescriptions: [],
        documents: [],
        visits: [],
        appointments: []
      });
    } catch (error) {
      console.error('Error loading animal details:', error);
      setSnackbarMessage('Eroare la incarcarea detaliilor animalului.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Quick Actions handlers
  const handleCreateAppointment = () => {
    setCreateAppointmentDialogOpen(true);
  };

  const handleStartVisit = () => {
    setStartVisitDialogOpen(true);
  };

  const handleIssuePrescription = () => {
    setIssuePrescriptionDialogOpen(true);
  };

  const handleAddDocument = () => {
    setAddDocumentDialogOpen(true);
  };

  const handleExportPDF = () => {
    setExportPDFDialogOpen(true);
  };

  const handleChangeOwner = () => {
    setAssignOwnerDialogOpen(true);
  };

  const handleEditAnimal = () => {
    setEditDialogOpen(true);
  };

  const handleAddNote = () => {
    setAddNoteDialogOpen(true);
  };

  const handleSaveSterilization = async () => {
    try {
      setSavingSterilization(true);
      
      await animalsService.updateAnimal(animalId, {
        isNeutered: sterilizationStatus,
        neuteredDate: sterilizationDate || undefined
      });
      
      // Update local state
      if (animal) {
        setAnimal({
          ...animal,
          spayedNeutered: sterilizationStatus
        });
      }
      
      setSnackbarMessage('Datele de sterilizare au fost salvate cu succes!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error saving sterilization data:', error);
      setSnackbarMessage('Eroare la salvarea datelor de sterilizare.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSavingSterilization(false);
    }
  };

  const handleSaveName = async () => {
    if (!newName.trim()) {
      setSnackbarMessage('Numele nu poate fi gol.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      setSavingName(true);
      
      await animalsService.updateAnimal(animalId, {
        name: newName.trim()
      });
      
      // Update local state
      if (animal) {
        setAnimal({
          ...animal,
          name: newName.trim()
        });
      }
      
      setEditingName(false);
      setSnackbarMessage('Numele animalului a fost actualizat cu succes!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error saving name:', error);
      setSnackbarMessage('Eroare la actualizarea numelui.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSavingName(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'deceased': return 'error';
      case 'transferred': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Activ';
      case 'deceased': return 'Decedat';
      case 'transferred': return 'Transferat';
      default: return status;
    }
  };

  const getFlagColor = (type: string) => {
    switch (type) {
      case 'allergy': return 'error';
      case 'warning': return 'warning';
      case 'chronic': return 'info';
      case 'intolerance': return 'secondary';
      default: return 'default';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Se incarca...</Typography>
      </Box>
    );
  }

  if (!animal) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography color="error">Animalul nu a fost gasit.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header fix cu informatii animal */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid size="grow">
              <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}>
                <PetsIcon sx={{ fontSize: 40 }} />
              </Avatar>
            </Grid>
            
            <Grid size="grow">
              <Stack spacing={1}>
                {editingName ? (
                  <Box display="flex" alignItems="center" gap={2}>
                    <TextField
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      variant="outlined"
                      size="small"
                      sx={{ 
                        '& .MuiInputBase-root': { 
                          fontSize: '2rem',
                          fontWeight: 'bold'
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleSaveName}
                      disabled={savingName}
                    >
                      {savingName ? 'Se salveaza...' : 'Salveaza'}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setEditingName(false);
                        setNewName(animal.name);
                      }}
                    >
                      Anuleaza
                    </Button>
                  </Box>
                ) : (
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="h4" component="h1">
                      {animal.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setEditingName(true)}
                      sx={{ ml: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                <Typography variant="h6" color="text.secondary">
                  {animal.species} • {animal.breed} • {animal.sex === 'male' ? 'Mascul' : 'Feminin'} • {animal.age} ani
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Proprietar: {animal.clientName} • Greutate: {animal.weight ? `${animal.weight} kg` : 'Necunoscuta'}
                </Typography>
              </Stack>
            </Grid>

            <Grid size="grow">
              <Stack spacing={1} alignItems="flex-end">
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<HospitalIcon />}
                  onClick={() => {
                    navigate(`/clinic/patients/${animalId}/medical`);
                    onClose?.();
                  }}
                >
                  Dosar medical complet
                </Button>
                <Chip 
                  label={getStatusLabel(animal.status)} 
                  color={getStatusColor(animal.status) as any}
                  size="small"
                />
                {animal.microchip && (
                  <Chip 
                    label={`Cip: ${animal.microchip}`} 
                    variant="outlined"
                    size="small"
                  />
                )}
                {animal.spayedNeutered && (
                  <Chip 
                    label="Castrat/Sterilizat" 
                    color="info"
                    size="small"
                  />
                )}
              </Stack>
            </Grid>
          </Grid>

          {/* Badges pentru flag-uri */}
          {animal.flags && animal.flags.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {animal.flags.map((flag) => (
                  <Tooltip key={flag.id} title={flag.notes || flag.label}>
                    <Chip
                      label={flag.label}
                      color={getFlagColor(flag.type) as any}
                      size="small"
                      icon={flag.type === 'allergy' ? <WarningIcon /> : undefined}
                    />
                  </Tooltip>
                ))}
              </Stack>
            </Box>
          )}

          {/* Quick Actions */}
          <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<CalendarIcon />}
              size="small"
              onClick={handleCreateAppointment}
            >
              Creeaza Programare
            </Button>
            <Button
              variant="outlined"
              startIcon={<HospitalIcon />}
              size="small"
              onClick={handleStartVisit}
            >
              Incepe Vizita
            </Button>
            <Button
              variant="outlined"
              startIcon={<ReceiptIcon />}
              size="small"
              onClick={handleIssuePrescription}
            >
              Emite Reteta
            </Button>
            <Button
              variant="outlined"
              startIcon={<AttachFileIcon />}
              size="small"
              onClick={handleAddDocument}
            >
              Adauga Document
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              size="small"
              onClick={handleExportPDF}
            >
              Exporta Fisa PDF
            </Button>
            <Button
              variant="outlined"
              startIcon={<PersonIcon />}
              size="small"
              onClick={handleChangeOwner}
            >
              Schimba Proprietar
            </Button>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              size="small"
              onClick={handleEditAnimal}
            >
              Editeaza
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Tab-uri */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            <Tab label="Overview" />
            <Tab label="Medical" />
            <Tab label="Vaccinari & Deparazitari" />
            <Tab label="Retete" />
            <Tab label="Alergii" />
            <Tab label="Reproducere" />
            <Tab label="Sterilizare" />
            <Tab label="Identificare & Documente" />
            <Tab label="Programari & Vizite" />
            <Tab label="Facturi & Plati" />
            <Tab 
              label="Date Irevocabile" 
              sx={{ 
                color: 'error.main', 
                '&.Mui-selected': { 
                  color: 'error.main',
                  backgroundColor: 'error.light',
                  opacity: 0.1
                }
              }} 
            />
          </Tabs>
        </Box>

        {/* Tab 1: Overview */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Card Identitate */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Identitate
                </Typography>
                <Stack spacing={1}>
                  <Typography><strong>Specie:</strong> {animal.species}</Typography>
                  <Typography><strong>Rasa:</strong> {animal.breed}</Typography>
                  <Typography><strong>Culoare:</strong> {animal.color || 'Necunoscuta'}</Typography>
                  <Typography><strong>Marcaje:</strong> {animal.markings || 'Niciunul'}</Typography>
                  <Typography><strong>Sex:</strong> {animal.sex === 'male' ? 'Mascul' : 'Feminin'}</Typography>
                  <Typography><strong>Data nasterii:</strong> {animal.dateOfBirth ? formatDateOnly(animal.dateOfBirth) : 'Necunoscuta'}</Typography>
                  <Typography><strong>Varsta:</strong> {animal.age} ani</Typography>
                  <Typography><strong>Status reproducere:</strong> {animal.spayedNeutered ? 'Castrat/Sterilizat' : 'Intact'}</Typography>
                  <Typography><strong>Microchip:</strong> {animal.microchip || 'Nu'}</Typography>
                  <Typography><strong>Status pacient:</strong> {getStatusLabel(animal.status)}</Typography>
                </Stack>
              </Paper>
            </Grid>

            {/* Card Ultimele Interactiuni */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Ultimele Interactiuni
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    Ultimele 5 vizite: Nu exista inregistrari
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ultimele 3 rezultate lab: Nu exista inregistrari
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ultimele retete emise: Nu exista inregistrari
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Vaccinari viitoare: Nu exista inregistrari
                  </Typography>
                </Stack>
              </Paper>
            </Grid>

            {/* Card Note Rapide */}
            <Grid size={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Note Rapide
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {animal.notes || 'Nu exista note rapide.'}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Button size="small" startIcon={<AddIcon />} onClick={handleAddNote}>
                    Adauga Nota
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 2: Medical */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Istoric Medical
          </Typography>
          <Alert severity="info">
            Sectiunea Medical va fi implementata in urmatoarea iteratie.
          </Alert>
        </TabPanel>

        {/* Tab 3: Vaccinari & Deparazitari */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Vaccinari & Deparazitari
          </Typography>
          <Alert severity="info">
            Sectiunea Vaccinari & Deparazitari va fi implementata in urmatoarea iteratie.
          </Alert>
        </TabPanel>

        {/* Tab 4: Retete */}
        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom>
            Retete & Prescriptii
          </Typography>
          <Alert severity="info">
            Sectiunea Retete va fi implementata in urmatoarea iteratie.
          </Alert>
        </TabPanel>

        {/* Tab 5: Alergii */}
        <TabPanel value={tabValue} index={4}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Alergii & Intolerante
            </Typography>
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Alergii Cunoscute
                    </Typography>
                    {animal.flags && animal.flags.length > 0 ? (
                      <Stack spacing={2}>
                        {animal.flags.map((flag) => (
                          <Paper key={flag.id} sx={{ p: 2 }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                              <Chip
                                label={flag.type}
                                color={getFlagColor(flag.type) as any}
                                size="small"
                              />
                              <Typography variant="h6">{flag.label}</Typography>
                              <Chip
                                label={flag.severity}
                                color={getSeverityColor(flag.severity) as any}
                                size="small"
                              />
                            </Stack>
                            {flag.notes && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {flag.notes}
                              </Typography>
                            )}
                          </Paper>
                        ))}
                      </Stack>
                    ) : (
                      <Alert severity="info">
                        Nu exista alergii sau intolerante inregistrate.
                      </Alert>
                    )}
                  </Box>

                  <Divider />

                  <Box>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        // TODO: Implementare adaugare alergie
                        console.log('Adauga alergie');
                      }}
                    >
                      Adauga Alergie
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </TabPanel>

        {/* Tab 6: Reproducere */}
        <TabPanel value={tabValue} index={5}>
          <AnimalReproTab 
            animalId={animal.id}
            animalGender={animal.sex}
            onOpenLitterDialog={() => setLitterDialogOpen(true)}
            onOpenPedigreeModal={() => setPedigreeModalOpen(true)}
          />
        </TabPanel>

        {/* Tab 7: Sterilizare */}
        <TabPanel value={tabValue} index={6}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Informatii Sterilizare
            </Typography>
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Status Sterilizare
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Button
                        variant={sterilizationStatus ? "contained" : "outlined"}
                        color="success"
                        onClick={() => setSterilizationStatus(true)}
                        disabled={savingSterilization}
                      >
                        DA
                      </Button>
                      <Button
                        variant={!sterilizationStatus ? "contained" : "outlined"}
                        color="error"
                        onClick={() => setSterilizationStatus(false)}
                        disabled={savingSterilization}
                      >
                        NU
                      </Button>
                      {sterilizationStatus && (
                        <Chip 
                          label="Sterilizat" 
                          color="success" 
                          size="small"
                          icon={<CheckCircleIcon />}
                        />
                      )}
                    </Stack>
                  </Box>

                  {sterilizationStatus && (
                    <Box>
                      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                        Data Sterilizarii
                      </Typography>
                      <TextField
                        type="date"
                        fullWidth
                        size="medium"
                        value={sterilizationDate}
                        onChange={(e) => setSterilizationDate(e.target.value)}
                        disabled={savingSterilization}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        inputProps={{
                          max: new Date().toISOString().split('T')[0] // Nu permite date în viitor
                        }}
                        helperText="Selectează data când a fost efectuată sterilizarea"
                      />
                    </Box>
                  )}

                  <Divider />

                  <Box display="flex" gap={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSaveSterilization}
                      disabled={savingSterilization}
                    >
                      {savingSterilization ? 'Se salveaza...' : 'Salveaza'}
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        setSterilizationStatus(animal?.spayedNeutered || false);
                        setSterilizationDate('');
                      }}
                      disabled={savingSterilization}
                    >
                      Reseteaza
                    </Button>
                  </Box>

                  {sterilizationStatus && sterilizationDate && (
                    <Alert severity="info">
                      <Typography variant="body2">
                        <strong>Data sterilizarii:</strong> {formatDateOnly(sterilizationDate)}
                      </Typography>
                    </Alert>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </TabPanel>

        {/* Tab 8: Identificare & Documente */}
        <TabPanel value={tabValue} index={7}>
          <Typography variant="h6" gutterBottom>
            Identificare & Documente
          </Typography>
          <Alert severity="info">
            Sectiunea Identificare & Documente va fi implementata in urmatoarea iteratie.
          </Alert>
        </TabPanel>

        {/* Tab 9: Programari & Vizite */}
        <TabPanel value={tabValue} index={8}>
          <Typography variant="h6" gutterBottom>
            Programari & Vizite
          </Typography>
          <Alert severity="info">
            Sectiunea Programari & Vizite va fi implementata in urmatoarea iteratie.
          </Alert>
        </TabPanel>

        {/* Tab 10: Facturi & Plati */}
        <TabPanel value={tabValue} index={9}>
          <Typography variant="h6" gutterBottom>
            Facturi & Plati
          </Typography>
          <Alert severity="info">
            Sectiunea Facturi & Plati va fi implementata in urmatoarea iteratie.
          </Alert>
        </TabPanel>

        {/* Tab 11: Date Irevocabile */}
        <TabPanel value={tabValue} index={10}>
          <Box>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                ⚠️ ATENȚIE: Aceste acțiuni sunt irevocabile!
              </Typography>
              <Typography variant="body2">
                Modificările efectuate în această secțiune nu pot fi anulate. 
                Asigură-te că ai confirmat toate detaliile înainte de a salva.
              </Typography>
            </Alert>

            <Card sx={{ border: '2px solid', borderColor: 'error.main', mb: 3 }}>
              <CardContent>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="h6" gutterBottom color="error">
                      🐾 Status Animal
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => {
                          // TODO: Implementare setare deces
                          console.log('Setare deces');
                        }}
                        sx={{ borderWidth: 2 }}
                      >
                        Marcheaza ca Decedat
                      </Button>
                      <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => {
                          // TODO: Implementare setare înstrăinare
                          console.log('Setare înstrăinare');
                        }}
                        sx={{ borderWidth: 2 }}
                      >
                        Marcheaza ca Instrăinat
                      </Button>
                    </Stack>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Data Evenimentului
                    </Typography>
                    <TextField
                      type="date"
                      fullWidth
                      size="medium"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        max: new Date().toISOString().split('T')[0]
                      }}
                      helperText="Selectează data când s-a întâmplat evenimentul"
                    />
                  </Box>

                  <Box>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Motiv / Detalii
                    </Typography>
                    <TextField
                      multiline
                      rows={4}
                      fullWidth
                      placeholder="Descrie circumstanțele evenimentului..."
                      helperText="Aceste informații vor fi înregistrate permanent"
                    />
                  </Box>

                  <Divider />

                  <Box>
                    <Button
                      variant="contained"
                      color="error"
                      size="large"
                      onClick={() => {
                        // TODO: Implementare salvare date irevocabile
                        console.log('Salvare date irevocabile');
                      }}
                      sx={{ 
                        fontWeight: 'bold',
                        px: 4,
                        py: 1.5
                      }}
                    >
                      CONFIRMĂ MODIFICAREA
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </TabPanel>
      </Card>

      {/* Snackbar pentru notificari */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Quick Actions Dialogs */}
      
      {/* Create Appointment Dialog */}
      {animal && (
        <CreateAppointmentDialog
          open={createAppointmentDialogOpen}
          onClose={() => setCreateAppointmentDialogOpen(false)}
          onSuccess={() => {
            setSnackbarMessage('Programarea a fost creata cu succes!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
          }}
          petId={animal.id}
          ownerId={animal.ownerId}
          petName={animal.name}
        />
      )}

      {/* Start Visit Dialog */}
      {animal && (
        <StartVisitDialog
          open={startVisitDialogOpen}
          onClose={() => setStartVisitDialogOpen(false)}
          onSuccess={() => {
            setSnackbarMessage('Vizita a fost inceputa cu succes!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
          }}
          petId={animal.id}
          ownerId={animal.ownerId}
          petName={animal.name}
        />
      )}

      {/* Issue Prescription Dialog */}
      <IssuePrescriptionDialog
        open={issuePrescriptionDialogOpen}
        onClose={() => setIssuePrescriptionDialogOpen(false)}
        onSuccess={() => {
          setSnackbarMessage('Reteta a fost emisa cu succes!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        }}
        petId={animal.id}
        ownerId={animal.ownerId}
        petName={animal.name}
      />

      {/* Add Document Dialog */}
      <AddDocumentDialog
        open={addDocumentDialogOpen}
        onClose={() => setAddDocumentDialogOpen(false)}
        onSuccess={() => {
          setSnackbarMessage('Documentul a fost incarcat cu succes!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        }}
        petId={animal.id}
        petName={animal.name}
      />

      {/* Export PDF Dialog */}
      <ExportPDFDialog
        open={exportPDFDialogOpen}
        onClose={() => setExportPDFDialogOpen(false)}
        onSuccess={() => {
          setSnackbarMessage('Fisa PDF a fost generata si descarcata cu succes!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        }}
        petId={animal.id}
        petName={animal.name}
      />

      {/* Add Note Dialog */}
      <Dialog open={addNoteDialogOpen} onClose={() => setAddNoteDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Adauga Nota pentru {animal?.name}</DialogTitle>
        <DialogContent>
          <Alert severity="info">
            Functionalitatea de adaugare nota va fi implementata in curand.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddNoteDialogOpen(false)}>Inchide</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Animal Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Editeaza {animal?.name}</DialogTitle>
        <DialogContent>
          <Alert severity="info">
            Functionalitatea de editare animal va fi implementata in curand.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Inchide</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Owner Dialog */}
      <Dialog open={assignOwnerDialogOpen} onClose={() => setAssignOwnerDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Schimba Proprietar pentru {animal?.name}</DialogTitle>
        <DialogContent>
          <Alert severity="info">
            Functionalitatea de schimbare proprietar va fi implementata in curand.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOwnerDialogOpen(false)}>Inchide</Button>
        </DialogActions>
      </Dialog>

      {/* Litter Dialog */}
      <LitterDialog
        open={litterDialogOpen}
        onClose={() => setLitterDialogOpen(false)}
        motherId={animal.sex === 'female' ? animal.id : undefined}
        fatherId={animal.sex === 'male' ? animal.id : undefined}
        onSuccess={() => {
          loadAnimalDetails();
          setSnackbarMessage('Fatare adaugata cu succes!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        }}
      />

      {/* Pedigree Modal */}
      <PedigreeModal
        open={pedigreeModalOpen}
        onClose={() => setPedigreeModalOpen(false)}
        animalId={animal.id}
        animalName={animal.name}
      />
    </Box>
  );
};

export default AnimalCard;
